import $ from 'jquery'
import {Core} from './core.js'

export class Forms {

  constructor() {
    this.classes = {
      field: 'floating-field',
      select: 'floating-field-select',
      label: 'floating-field-label',
      focused: 'is-focused',
      filled: 'is-filled',
      active: 'is-active',
      errorEmpty: 'is-empty',
    }

    this.providers = [
      {
        type: 'marketo',
        form: 'form.mktoForm',
        container: '.mktoFieldWrap',
        selectContainer: '.mktoFieldWrap',
        customSelect: true
      },
      {
        type: 'hubspot',
        form: '.hbspt-form form',
        container: '.hs-form-field',
        selectContainer: '.hs-form-field',
        customSelect: true
      },
      {
        type: 'acton',
        form: 'form.ao-form',
        container: '.ao-input-block, .ao-textarea-block',
        selectContainer: '.ao-combo-block',
        customSelect: false
      },
      {
        type: 'activecampaign',
        form: 'form._form',
        container: '._form_element',
        selectContainer: '._form_element',
        customSelect: true
      },
      {
        type: 'odoo',
        form: 'form.odoo-form',
        container: '.odoo-field',
        selectContainer: '.odoo-field-select',
        customSelect: false
      },
      {
        type: 'odoo',
        form: 'form.odoo-form',
        container: '.odoo-field',
        selectContainer: '.odoo-field-select',
        customSelect: false,
        submit: 'button.odoo-submit'
      },
    ]

    // Marketo renseigne `data-original-placeholder` de façon asynchrone : si des
    // labels restent vides, on relance quelques passes espacées pour les remplir.
    this.labelRetry = {timer: null, count: 0, max: 5, delay: 300}

    // Types d'input à ignorer (on ne traite que les champs de saisie texte).
    this.skippedTypes = [
      'hidden',
      'checkbox',
      'radio',
      'button',
      'image',
      'file',
      'reset',
      'submit'
    ]

    if (Core.isAdmin() || document.body.classList.contains('shine-cegid-coec')) {
      return
    }

    $(document).ready(this.init.bind(this))
  }

  init() {
    this.process()
    this.observe()
  }

  /**
   * Re-scanne le document quand le DOM change
   */
  observe() {
    let scheduled = false

    const observer = new MutationObserver((mutations) => {
      if (this.hasNewControl(mutations)) {
        this.labelRetry.count = 0
      }

      if (scheduled) {
        return
      }

      scheduled = true
      window.requestAnimationFrame(() => {
        scheduled = false
        this.process()
      })
    })

    observer.observe(document.body, {childList: true, subtree: true})
  }

  /**
   * Indique si une mutation a ajouté un vrai champ de saisie (directement ou
   * dans un sous-arbre inséré). On ignore nos propres insertions (label, span,
   * vidéo de bouton) pour ne pas réarmer le retry inutilement.
   */
  hasNewControl(mutations) {
    const selector = 'input, textarea, select'

    return mutations.some((mutation) =>
      Array.from(mutation.addedNodes).some((node) => {
        if (node.nodeType !== 1) {
          return false
        }

        return node.matches(selector) || node.querySelector(selector)
      })
    )
  }

  process() {
    this.providers.forEach((provider) => {
      $(provider.form).each((index, form) => {
        const $form = $(form)

        this.cleanStyles($form)

        $form.find('input, textarea').each((i, control) => {
          this.processControl($(control), provider)
        })

        $form.find('select').each((i, select) => {
          this.processSelect($(select), provider)
        })

        if (provider.type === 'acton') {
          this.hideEmptyActonErrors($form)
        }
      })
    })

    this.scheduleLabelRetry()
  }

  /**
   * Tant que des labels restent vides (placeholder Marketo pas encore posé), on
   * replanifie une passe espacée. On s'arrête dès que tout est rempli ou après un
   * nombre maximal de tentatives.
   */
  scheduleLabelRetry() {
    if (this.labelRetry.timer) {
      return
    }

    const hasEmpty = $('.' + this.classes.label)
      .filter((i, el) => !this.labelText($(el))).length > 0

    if (!hasEmpty) {
      this.labelRetry.count = 0
      return
    }

    if (this.labelRetry.count >= this.labelRetry.max) {
      return
    }

    this.labelRetry.count++
    this.labelRetry.timer = window.setTimeout(() => {
      this.labelRetry.timer = null
      this.process()
    }, this.labelRetry.delay)
  }

  /**
   * Supprime les styles imposés par les embeds tiers pour laisser notre CSS
   * gouverner : les balises <style> injectées (dans le form et son parent) et
   * les attributs `style` inline. On conserve les `display: none` (champs
   * cachés fonctionnels : sources HubSpot, thank-you ActiveCampaign, etc.).
   */
  cleanStyles($form) {
    $form.find('style').remove()

    $form.parent().children('style').each((i, el) => {
      if ($(el).closest('main').length) {
        el.remove()
      }
    })

    const strip = (el) => {
      const style = el.getAttribute('style')

      if (!style || /display\s*:\s*none/i.test(style)) {
        return
      }

      el.removeAttribute('style')
    }

    strip($form[0])
    $form.find('[style]').each((i, el) => strip(el))
  }

  /**
   * Act-On laisse des `.ao-form-error-message` dans le DOM avec un simple
   * &nbsp; quand il n'y a pas d'erreur, ce qui crée un espace vide visible. On
   * masque ceux dont le contenu se résume à des espaces (insécables compris) et
   * on les ré-affiche dès qu'un vrai message apparaît.
   */
  hideEmptyActonErrors($form) {
    $form.find('.ao-form-error-message').each((i, el) => {
      const $message = $(el)
      const isEmpty = $message.text().replace(/\u00a0/g, '').trim() === ''

      $message.toggleClass(this.classes.errorEmpty, isEmpty)
    })
  }

  processControl($control, provider) {
    if (!this.isEligible($control)) {
      return
    }

    const $container = $control.closest(provider.container)

    if (!$container.length) {
      return
    }

    // Le label est (re)tenté à chaque passage : son texte peut n'arriver qu'après
    // coup (placeholder Marketo asynchrone).
    const $label = this.ensureLabel($control, $container)
    $label.addClass(this.classes.label)

    const id = $control.attr('id')
    if (id && !$label.attr('for')) {
      $label.attr('for', id)
    }

    // Setup unique du champ (état, suppression du placeholder natif).
    if (!$container.hasClass(this.classes.field)) {
      $container.addClass(this.classes.field)

      // On retire le placeholder natif : au repos, c'est le label qui occupe sa
      // place (on le mémorise au cas où).
      const placeholder = $control.attr('placeholder')
      if (placeholder) {
        $control.attr('data-original-placeholder', placeholder)
        $control.removeAttr('placeholder')
      }

      this.bindState($control, $container)
    }
  }

  isEligible($control) {
    // Champ interne à un select custom (ex. recherche pays) : on l'ignore.
    if ($control.closest('.selector').length) {
      return false
    }

    if ($control.is('textarea')) {
      return true
    }

    const type = ($control.attr('type') || 'text').toLowerCase()

    return !this.skippedTypes.includes(type)
  }

  /**
   * Récupère le label existant ou le crée à partir du placeholder.
   * - HubSpot : <label> déjà présent et rempli -> on le réutilise tel quel.
   * - Act-On  : <label.ao-form-label> présent mais vide -> on le remplit.
   * - Marketo : pas de label pour les champs texte -> on le crée.
   */
  ensureLabel($control, $container) {
    // On récupère en priorité le label qu'on a déjà créé/marqué lors d'un passage
    // précédent (sa classe), sinon un éventuel label natif (HubSpot, Act-On).
    let $label = $container.find('label.' + this.classes.label).first()

    if (!$label.length) {
      $label = $container.children('label').first()
    }

    // On considère un label comme déjà rempli seulement s'il porte un VRAI texte :
    // Marketo crée le label avec uniquement l'astérisque requis (.mktoAsterix) et
    // n'y injecte le nom du champ qu'après coup, d'où ce label « * » à compléter.
    if ($label.length && this.labelText($label)) {
      return $label
    }

    const text = this.getLabelText($control, $label)

    if ($label.length) {
      // On ne remplit que si on a enfin un texte, pour pouvoir retenter plus tard.
      if (text) {
        this.setLabelText($label, text)
      }
      return $label
    }

    // On insère le label juste avant le champ pour qu'il partage son conteneur.
    $label = $('<label></label>').text(text)
    $label.insertBefore($control)

    return $label
  }

  /**
   * Texte « utile » d'un label, astérisque requis exclu : Marketo place le « * »
   * dans un élément séparé (.mktoAsterix) et certains thèmes l'ajoutent en texte
   * brut. Un label réduit à « * » est donc considéré comme vide.
   */
  labelText($label) {
    return $label
      .clone()
      .find('.mktoAsterix').remove().end()
      .text()
      .replace(/\*/g, '')
      .trim()
  }

  /**
   * Pose le texte du label sans écraser l'astérisque requis : on le détache, on
   * remplit, puis on le ré-insère à sa place (Marketo le met avant le libellé).
   */
  setLabelText($label, text) {
    const $aster = $label.find('.mktoAsterix').first().detach()

    $label.text(text)

    if ($aster.length) {
      $label.prepend($aster)
    }
  }

  getLabelText($control, $existingLabel) {
    // Marketo stocke le placeholder dans `data-original-placeholder` d'où le fallback sur cet attribut.
    const placeholder = $control.attr('placeholder') || $control.attr('data-original-placeholder')

    if (placeholder && placeholder.trim()) {
      return placeholder.trim()
    }

    const existing = $existingLabel.length ? this.labelText($existingLabel) : ''
    if (existing) {
      return existing
    }

    return ''
  }

  bindState($control, $container) {
    // Les champs date/heure affichent toujours leur format natif (jj/mm/aaaa…) :
    // ils ne sont jamais visuellement vides, donc le label doit rester en haut en
    // permanence, sans attendre le focus ou une saisie.
    const alwaysActive = this.isAlwaysActive($control)

    const update = () => {
      const value = $control.val()
      const filled = !!value && value.trim() !== ''
      const focused = $control.is(':focus')

      $container.toggleClass(this.classes.filled, filled)
      $container.toggleClass(this.classes.focused, focused)
      $container.toggleClass(this.classes.active, alwaysActive || filled || focused)
    }

    $control.on('focus blur input change', update)

    update()

    // Rattrape l'auto-remplissage navigateur/gestionnaire de mots de passe qui
    // ne déclenche pas toujours d'événement input.
    window.setTimeout(update, 300)
  }

  /**
   * Types de champ qui exposent toujours un format natif (jj/mm/aaaa, hh:mm…) et
   * ne sont donc jamais visuellement vides : le label doit rester en haut en
   * permanence.
   */
  isAlwaysActive($control) {
    const alwaysActiveTypes = ['date', 'datetime-local', 'time', 'month', 'week']
    const type = ($control.attr('type') || '').toLowerCase()

    return alwaysActiveTypes.includes(type)
  }

  /**
   * Pas de floating label sur les selects : on marque seulement le conteneur
   * (.floating-field-select) pour que le CSS masque le label. La 1re option du
   * select sert d'intitulé.
   */
  processSelect($select, provider) {
    const $container = $select.closest(provider.selectContainer)

    if (!$container.length || $container.hasClass(this.classes.select)) {
      return
    }

    $container.addClass(this.classes.select)
  }
}
